import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { api } from "../config/api";
import { useBooking } from "../contexts/BookingContext";
import { useLocation } from "../contexts/LocationContext";
import AgeWarningModal from "./AgeWarningModal";

// ─── All visual styling handled by headerbook.css via .qs__* classes ──────────
// JS only controls what CSS cannot: chevron rotation + menu zIndex
const selectStyles = {
  control: (b) => ({ ...b }),
  valueContainer: (b) => ({ ...b }),
  singleValue: (b) => ({ ...b }),
  placeholder: (b) => ({ ...b }),
  input: (b) => ({ ...b }),
  menu: (b) => ({ ...b, zIndex: 9999 }),
  menuList: (b) => ({ ...b }),
  option: (b) => ({ ...b }),

  menuPortal: (base) => ({
    ...base,
    zIndex: 9999999,
  }),

  menu: (base) => ({
    ...base,
    zIndex: 9999999,
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (b, s) => ({
    ...b,
    transition: "transform 0.2s ease",
    transform: s.selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
  }),
  clearIndicator: (b) => ({ ...b }),
  loadingIndicator: (b) => ({ ...b }),
  noOptionsMessage: (b) => ({ ...b }),
  loadingMessage: (b) => ({ ...b }),
};

const sharedProps = {
  styles: selectStyles,
  classNamePrefix: "qs",
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  isClearable: true,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const LOOK_AHEAD = 14;

const toDateStr = (offset) => {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split("T")[0];
};

const fmtDateLabel = (ds) =>
  new Date(ds).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

const fmtTime = (show) => {
  if (show.time && show.time !== "TBA") return show.time;
  const raw = show.showDateTime || show.startDateTime;
  if (raw) {
    const d = new Date(raw);
    if (!isNaN(d))
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return "TBA";
};

const buildShowtimeOpts = (showtimes = [], dateValue, cinemaId, movieId) => {
  const seen = new Set();
  return showtimes
    .filter((s) => {
      const raw = s.showDateTime || s.startDateTime;
      if (raw && new Date(raw).toISOString().split("T")[0] !== dateValue)
        return false;
      if (!raw && s.date && s.date !== dateValue) return false;
      const sid = String(s.sessionId || s.id || s.vistaShowId || "");
      if (sid && seen.has(sid)) return false;
      if (sid) seen.add(sid);
      return true;
    })
    .map((s) => {
      const time = fmtTime(s);
      const sid =
        s.sessionId ||
        s.id ||
        s.vistaShowId ||
        `${cinemaId}-${movieId}-${time}-${s.screenName || ""}`;
      let label = time;
      if (s.screenName) label += ` · ${s.screenName}`;
      if (s.screenType && s.screenType !== "regular")
        label += ` · ${s.screenType}`;
      return { value: String(sid), label, raw: s };
    });
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function QuickBookNew({ closeMobileQB }) {
  const { clearBooking, setVistaTransactionId } = useBooking();
  const { userLocation, formatDistance, getLocationForAPI, selectedCity } =
    useLocation();
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 992 : true,
  );

  const navigate = useNavigate();

  // tab ref = source of truth for async callbacks (never stale)
  // tab state = triggers re-render
  // const tabRef = useRef("movie");
  // const [tab, setTab] = useState("movie");

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tabRef = useRef(isDesktop ? "movie" : null);
  const [tab, setTab] = useState(isDesktop ? "movie" : null);

  const isTab = useCallback((t) => tabRef.current === t || tab === t, [tab]);

  // ── Global lists ──────────────────────────────────────────────────────────
  const [allMovies, setAllMovies] = useState([]);
  const [allCinemas, setAllCinemas] = useState([]);
  const [loadingAllMovies, setLoadingAllMovies] = useState(false);
  const [loadingAllCinemas, setLoadingAllCinemas] = useState(false);

  // ── Step selections ───────────────────────────────────────────────────────
  const [movieSel, setMovieSel] = useState(null);
  const [cinemaSel, setCinemaSel] = useState(null);
  const [dateSel, setDateSel] = useState(null);
  const [showtimeSel, setShowtimeSel] = useState(null);

  // ── Derived option lists ──────────────────────────────────────────────────
  const [dates, setDates] = useState([]);
  const [cinemaList, setCinemaList] = useState([]);
  const [movieList, setMovieList] = useState([]);
  const [showtimes, setShowtimes] = useState([]);

  // ── Loading flags ─────────────────────────────────────────────────────────
  const [loadingDates, setLoadingDates] = useState(false);
  const [loadingCinemas2, setLoadingCinemas2] = useState(false);
  const [loadingMovies2, setLoadingMovies2] = useState(false);
  const [loadingShowtimes, setLoadingShowtimes] = useState(false);

  // ── Age modal ─────────────────────────────────────────────────────────────
  const [ageModal, setAgeModal] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);

  const preventDetailsClose = (e) => {
    e.stopPropagation();
  };

  // ── Tab switch ────────────────────────────────────────────────────────────
  // onPointerDown fires before blur events — prevents click being swallowed
  const switchTab = useCallback(
    (e, next) => {
      e.preventDefault();
      e.stopPropagation();
      if (tabRef.current === next) {
        if (!isDesktop) {
          tabRef.current = null;
          setTab(null);
          setMovieSel(null);
          setCinemaSel(null);
          setDateSel(null);
          setShowtimeSel(null);
          setDates([]);
          setCinemaList([]);
          setMovieList([]);
          setShowtimes([]);
        }
        return;
      }
      tabRef.current = next;
      setTab(next);
      setMovieSel(null);
      setCinemaSel(null);
      setDateSel(null);
      setShowtimeSel(null);
      setDates([]);
      setCinemaList([]);
      setMovieList([]);
      setShowtimes([]);
      setLoadingDates(false);
      setLoadingCinemas2(false);
      setLoadingMovies2(false);
      setLoadingShowtimes(false);
    },
    [isDesktop],
  );

  // ══════════════════════════════════════════════════════════════════════════
  // Fetch global lists
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    let alive = true;
    setLoadingAllMovies(true);
    api
      .getMovies({ limit: 100 })
      .then((res) => {
        if (!alive) return;
        setAllMovies(
          (Array.isArray(res) ? res : [])
            .map((m) => ({ ...m, bookingId: m.vistaMovieId || m.id }))
            .filter(
              (m) => m.status === "Now Showing" || m.isNowShowing === true,
            )
            .sort((a, b) => a.title.localeCompare(b.title)),
        );
      })
      .catch(() => alive && setAllMovies([]))
      .finally(() => alive && setLoadingAllMovies(false));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoadingAllCinemas(true);
    const filters = { limit: 100, sortBy: "name" };
    const loc = getLocationForAPI();
    if (loc) {
      filters.latitude = loc.lat;
      filters.longitude = loc.lng;
    }
    api
      .getCinemas(filters)
      .then((res) => {
        if (!alive) return;
        setAllCinemas(
          (Array.isArray(res) ? res : [])
            .map((c) => ({
              ...c,
              vistaCinemaId:
                c.vistaCinemaId || c.vistacinemaid || c.vista_cinema_id || null,
              location: c.location || c.city || "",
              distance: c.distance ?? null,
            }))
            .sort((a, b) => {
              if (a.distance != null && b.distance != null)
                return a.distance - b.distance;
              if (a.distance != null) return -1;
              if (b.distance != null) return 1;
              return a.name.localeCompare(b.name);
            }),
        );
      })
      .catch(() => alive && setAllCinemas([]))
      .finally(() => alive && setLoadingAllCinemas(false));
    return () => {
      alive = false;
    };
  }, [userLocation, selectedCity]);

  // ══════════════════════════════════════════════════════════════════════════
  // MOVIE TAB — Step 2: movie selected → scan dates
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (tab !== "movie" || !movieSel || !allCinemas.length) {
      if (tab === "movie") setDates([]);
      return;
    }

    let alive = true;
    setLoadingDates(true);
    setDates([]);

    const fetchDates = async () => {
      try {
        const foundDates = new Set();

        const MAX_DATES = 5; // how many dates to show
        const MAX_CINEMAS = 5; // limit cinemas per date

        // ✅ ONLY CHECK 5 DAYS (no 10/14 loop)
        const dateStrs = Array.from({ length: MAX_DATES }, (_, i) =>
          toDateStr(i),
        );

        for (const dateStr of dateStrs) {
          for (const c of allCinemas.slice(0, MAX_CINEMAS)) {
            try {
              const sch = await api.getCinemaSchedule(c.id, dateStr);

              if (!alive || !isTab("movie")) return;

              const hasMovie = sch?.movies?.some((m) => {
                const idMatch =
                  String(m.id) === String(movieSel.value) ||
                  String(m.vistaMovieId) === String(movieSel.value) ||
                  String(m.bookingId) === String(movieSel.value);

                const titleMatch =
                  m.title?.toLowerCase().trim() ===
                  movieSel.label?.toLowerCase().trim();

                return (idMatch || titleMatch) && m.showtimes?.length > 0;
              });

              if (hasMovie) {
                foundDates.add(dateStr);
                break; // ✅ stop checking more cinemas for this date
              }
            } catch (err) {
              console.warn("Date scan error:", err);
            }
          }
        }

        if (!alive || !isTab("movie")) return;

        const formatted = Array.from(foundDates)
          .sort()
          .map((ds) => ({
            value: ds,
            label: fmtDateLabel(ds),
          }));

        setDates(formatted);
      } catch (error) {
        console.error("Date fetch error:", error);
        setDates([]);
      } finally {
        if (alive) setLoadingDates(false);
      }
    };

    fetchDates();

    return () => {
      alive = false;
    };
  }, [movieSel, tab, allCinemas]);

  // ══════════════════════════════════════════════════════════════════════════
  // MOVIE TAB — Step 3: movie + date → find cinemas
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (tab !== "movie" || !movieSel || !dateSel) {
      if (tab === "movie") setCinemaList([]);
      return;
    }

    let alive = true;
    setLoadingCinemas2(true);
    setCinemaList([]);

    const fetchCinemas = async () => {
      try {
        const matches = [];

        for (const c of allCinemas) {
          try {
            const sch = await api.getCinemaSchedule(c.id, dateSel.value);

            if (!alive || !isTab("movie")) return;

            const hasMovie = sch?.movies?.some((m) => {
              const idMatch =
                String(m.id) === String(movieSel.value) ||
                String(m.vistaMovieId) === String(movieSel.value) ||
                String(m.bookingId) === String(movieSel.value);

              const titleMatch =
                m.title?.toLowerCase().trim() ===
                movieSel.label?.toLowerCase().trim();

              return (
                (idMatch || titleMatch) &&
                Array.isArray(m.showtimes) &&
                m.showtimes.length > 0
              );
            });

            if (hasMovie) {
              matches.push(c);
            }
          } catch (err) {
            console.warn("Schedule failed for cinema:", c.id);
          }
        }

        if (!alive || !isTab("movie")) return;

        setCinemaList(matches);
      } catch (error) {
        console.error("Cinema fetch error:", error);
        setCinemaList([]);
      } finally {
        if (alive) setLoadingCinemas2(false);
      }
    };

    fetchCinemas();

    return () => {
      alive = false;
    };
  }, [movieSel, dateSel, tab, allCinemas]);

  // ══════════════════════════════════════════════════════════════════════════
  // MOVIE TAB — Step 4: movie + date + cinema → showtimes
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (tab !== "movie" || !movieSel || !dateSel || !cinemaSel) {
      if (tab === "movie") setShowtimes([]);
      return;
    }
    let alive = true;
    setLoadingShowtimes(true);
    setShowtimes([]);
    api
      .getCinemaSchedule(cinemaSel.value, dateSel.value)
      .then((sch) => {
        if (!alive || !isTab("movie")) return;
        const mov = sch?.movies?.find((m) => {
          return (
            String(m.id) === String(movieSel.value) ||
            String(m.vistaMovieId) === String(movieSel.value) ||
            String(m.bookingId) === String(movieSel.value) ||
            m.title?.toLowerCase().trim() ===
              movieSel.label?.toLowerCase().trim()
          );
        });
        setShowtimes(
          buildShowtimeOpts(
            mov?.showtimes,
            dateSel.value,
            cinemaSel.value,
            movieSel.value,
          ),
        );
      })
      .catch(() => alive && setShowtimes([]))
      .finally(() => alive && setLoadingShowtimes(false));
    return () => {
      alive = false;
    };
  }, [movieSel, dateSel, cinemaSel, tab]);

  // ══════════════════════════════════════════════════════════════════════════
  // CINEMA TAB — Step 2: cinema selected → scan dates
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (tab !== "cinema" || !cinemaSel) {
      if (tab === "cinema") setDates([]);
      return;
    }

    let alive = true;
    setLoadingDates(true);
    setDates([]);

    const fetchDates = async () => {
      try {
        const found = new Set();

        const MAX_DATES = 5; // ✅ only 5 days
        const dateStrs = Array.from({ length: MAX_DATES }, (_, i) =>
          toDateStr(i),
        );

        for (const ds of dateStrs) {
          try {
            const sch = await api.getCinemaSchedule(cinemaSel.value, ds);

            if (!alive || !isTab("cinema")) return;

            if (sch?.movies?.some((m) => m.showtimes?.length > 0)) {
              found.add(ds);
            }
          } catch (err) {
            console.warn("Cinema date error:", err);
          }
        }

        if (!alive || !isTab("cinema")) return;

        setDates(
          Array.from(found)
            .sort()
            .map((ds) => ({
              value: ds,
              label: fmtDateLabel(ds),
            })),
        );
      } catch (error) {
        console.error("Cinema date fetch error:", error);
        setDates([]);
      } finally {
        if (alive) setLoadingDates(false);
      }
    };

    fetchDates();

    return () => {
      alive = false;
    };
  }, [cinemaSel, tab]);
  // ══════════════════════════════════════════════════════════════════════════
  // CINEMA TAB — Step 3: cinema + date → movies
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (tab !== "cinema" || !cinemaSel || !dateSel) {
      if (tab === "cinema") setMovieList([]);
      return;
    }
    let alive = true;
    setLoadingMovies2(true);
    setMovieList([]);
    api
      .getCinemaSchedule(cinemaSel.value, dateSel.value)
      .then((sch) => {
        if (!alive || !isTab("cinema")) return;
        setMovieList(
          (sch?.movies || [])
            .filter((m) => m.showtimes?.length > 0)
            .sort((a, b) => (a.title || "").localeCompare(b.title || "")),
        );
      })
      .catch(() => alive && setMovieList([]))
      .finally(() => alive && setLoadingMovies2(false));
    return () => {
      alive = false;
    };
  }, [cinemaSel, dateSel, tab]);

  // ══════════════════════════════════════════════════════════════════════════
  // CINEMA TAB — Step 4: cinema + date + movie → showtimes
  // ══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (tab !== "cinema" || !cinemaSel || !dateSel || !movieSel) {
      if (tab === "cinema") setShowtimes([]);
      return;
    }
    let alive = true;
    setLoadingShowtimes(true);
    setShowtimes([]);
    api
      .getCinemaSchedule(cinemaSel.value, dateSel.value)
      .then((sch) => {
        if (!alive || !isTab("cinema")) return;
        const mov = sch?.movies?.find(
          (m) => m.id === movieSel.value || m.vistaMovieId === movieSel.value,
        );
        setShowtimes(
          buildShowtimeOpts(
            mov?.showtimes,
            dateSel.value,
            cinemaSel.value,
            movieSel.value,
          ),
        );
      })
      .catch(() => alive && setShowtimes([]))
      .finally(() => alive && setLoadingShowtimes(false));
    return () => {
      alive = false;
    };
  }, [cinemaSel, dateSel, movieSel, tab]);

  // ── Option arrays ─────────────────────────────────────────────────────────
  const allMovieOpts = allMovies.map((m) => ({
    value: m.id,
    label: m.title,
    raw: m,
  }));

  const mkCinemaLabel = (c) => {
    let label = c.name;
    if (c.location) label += ` (${c.location})`;
    if (c.distance != null && formatDistance)
      label += ` — ${formatDistance(c.distance)}`;
    return label;
  };
  const allCinemaOpts = allCinemas.map((c) => ({
    value: c.id,
    label: mkCinemaLabel(c),
    raw: c,
  }));
  const cinemaListOpts = cinemaList.map((c) => ({
    value: c.id,
    label: mkCinemaLabel(c),
    raw: c,
  }));
  const movieListOpts = movieList.map((m) => ({
    value: m.id || m.vistaMovieId,
    label: m.title,
    raw: m,
  }));

  // ── Submit ────────────────────────────────────────────────────────────────
  const canSubmit = !!(movieSel && cinemaSel && dateSel && showtimeSel);

  const doNavigate = (data) => {
    clearBooking();
    setVistaTransactionId(null);
    navigate("/seat-selection", { state: data, replace: false });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!tab) return;
    // Lookup the full movie data (has poster/banner/genre/director/starring/etc.)
    // from allMovies — the source of truth populated from api.getMovies().
    // Important: match by vistaMovieId (and bookingId) too, because when booking
    // via the Cinema tab, movieSel.raw comes from the cinema schedule API and is
    // sparse (only id/title/showtimes) — without this lookup, the seat selection
    // page would show no poster, no genre tags, etc.
    //
    // Compare as strings to avoid type-mismatch issues (e.g. movieSel.value is
    // "10020111000003" but allMovies[i].vistaMovieId might be a number, or vice
    // versa). Also fall back to title match as a last resort.
    const target = String(movieSel.value || "");
    const fullMovie = allMovies.find((m) => {
      if (!m) return false;
      if (String(m.id) === target) return true;
      if (String(m.vistaMovieId) === target) return true;
      if (String(m.bookingId) === target) return true;
      // Last-resort: match by title (case-insensitive, trimmed)
      if (
        movieSel.label &&
        m.title &&
        String(m.title).toLowerCase().trim() ===
          String(movieSel.label).toLowerCase().trim()
      ) {
        return true;
      }
      return false;
    });

    // Schedule data from the cinema API (has the showtimes for this cinema+date,
    // but may be missing poster/genre/director/etc.).
    const scheduleMovie =
      movieSel.raw ||
      movieList.find(
        (m) => m.id === movieSel.value || m.vistaMovieId === movieSel.value,
      );

    // Merge: full movie data wins (it has all the rich fields), but keep any
    // schedule-specific fields (e.g. showtimes) from scheduleMovie when missing.
    const movieRaw = fullMovie
      ? { ...scheduleMovie, ...fullMovie }
      : scheduleMovie;
    const cinemaRaw =
      cinemaSel.raw ||
      allCinemas.find((c) => c.id === cinemaSel.value) ||
      cinemaList.find((c) => c.id === cinemaSel.value);
    const data = {
      movie: movieRaw,
      theater: cinemaRaw,
      showtime: {
        ...showtimeSel.raw,
        date: showtimeSel.raw?.date || dateSel.value,
        time: showtimeSel.raw?.time || showtimeSel.label,
      },
      selectedDate: dateSel.value,
      showQuantityModal: true,
      fromQuickBooking: true,
    };
    const rating = (
      movieRaw?.ageRating ||
      movieRaw?.rating ||
      ""
    ).toUpperCase();
    if (rating === "A") {
      setPendingNav(data);
      setAgeModal(true);
    } else doNavigate(data);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // tab is the single source of truth — flips instantly on pointer down,
  // so placeholders update before any async work starts.
  return (
    <>
      <div
        onPointerDown={preventDetailsClose}
        onTouchStart={preventDetailsClose}
      >
        <div className="qb-mobile-header">
          <span>Quick Booking</span>
        </div>
        <form className="booking-form" onSubmit={handleSubmit}>
          {/* Tab toggle */}
          <div className="booking-options">
            <h6
              onPointerDown={(e) => {
                e.stopPropagation();
                navigate("/show-times");
              }}
              style={{ cursor: "pointer" }}
            >
              Quick Book
            </h6>
            <div className="th-tab-toggle">
              <button
                type="button"
                className={tab === "movie" ? "tab-btn active" : "tab-btn"}
                onPointerDown={(e) => switchTab(e, "movie")}
              >
                Movie
              </button>
              <button
                type="button"
                className={tab === "cinema" ? "tab-btn active" : "tab-btn"}
                onPointerDown={(e) => switchTab(e, "cinema")}
              >
                Cinema
              </button>
            </div>
          </div>

          {/* Dropdowns — key includes tab so React fully remounts on switch */}
          {tab && ( //quickbook initial mobile change
            <div className="booking-fields">
              {tab === "movie" ? (
                // ── MOVIE TAB: Movie → Date → Cinema → Showtime ──────────────
                <>
                  <Select
                    {...sharedProps}
                    key="mv-movie"
                    options={allMovieOpts}
                    value={movieSel}
                    onChange={(opt) => {
                      setMovieSel(opt);
                      setDateSel(null);
                      setCinemaSel(null);
                      setShowtimeSel(null);
                      setDates([]);
                      setCinemaList([]);
                      setShowtimes([]);
                    }}
                    placeholder="Select Movie"
                    isLoading={loadingAllMovies}
                    isSearchable
                    noOptionsMessage={() => "No movies available"}
                    loadingMessage={() => "Loading…"}
                  />
                  <Select
                    {...sharedProps}
                    key="mv-date"
                    options={dates}
                    value={dateSel}
                    onChange={(opt) => {
                      setDateSel(opt);
                      setCinemaSel(null);
                      setShowtimeSel(null);
                      setCinemaList([]);
                      setShowtimes([]);
                    }}
                    placeholder="Select Date"
                    isDisabled={!movieSel || loadingDates}
                    isLoading={loadingDates}
                    isSearchable={false}
                    noOptionsMessage={() => "No dates available"}
                  />
                  <Select
                    {...sharedProps}
                    key="mv-cinema"
                    options={cinemaListOpts}
                    value={cinemaSel}
                    onChange={(opt) => {
                      setCinemaSel(opt);
                      setShowtimeSel(null);
                      setShowtimes([]);
                    }}
                    placeholder="Select Cinema"
                    isDisabled={!dateSel || loadingCinemas2}
                    isLoading={loadingCinemas2}
                    isSearchable
                    noOptionsMessage={() => "No cinemas available"}
                    loadingMessage={() => "Finding…"}
                  />
                  <Select
                    {...sharedProps}
                    key="mv-time"
                    options={showtimes}
                    value={showtimeSel}
                    onChange={setShowtimeSel}
                    placeholder="Select Time"
                    isDisabled={!cinemaSel || loadingShowtimes}
                    isLoading={loadingShowtimes}
                    isSearchable={false}
                    noOptionsMessage={() => "No showtimes"}
                    loadingMessage={() => "Loading…"}
                  />
                </>
              ) : (
                // ── CINEMA TAB: Cinema → Date → Movie → Showtime ─────────────
                <>
                  <Select
                    {...sharedProps}
                    key="ci-cinema"
                    options={allCinemaOpts}
                    value={cinemaSel}
                    onChange={(opt) => {
                      setCinemaSel(opt);
                      setDateSel(null);
                      setMovieSel(null);
                      setShowtimeSel(null);
                      setDates([]);
                      setMovieList([]);
                      setShowtimes([]);
                    }}
                    placeholder="Select Cinema"
                    isLoading={loadingAllCinemas}
                    isSearchable
                    noOptionsMessage={() => "No cinemas available"}
                    loadingMessage={() => "Loading…"}
                  />
                  <Select
                    {...sharedProps}
                    key="ci-date"
                    options={dates}
                    value={dateSel}
                    onChange={(opt) => {
                      setDateSel(opt);
                      setMovieSel(null);
                      setShowtimeSel(null);
                      setMovieList([]);
                      setShowtimes([]);
                    }}
                    placeholder="Select Date"
                    isDisabled={!cinemaSel || loadingDates}
                    isLoading={loadingDates}
                    isSearchable={false}
                    noOptionsMessage={() => "No dates available"}
                  />
                  <Select
                    {...sharedProps}
                    key="ci-movie"
                    options={movieListOpts}
                    value={movieSel}
                    onChange={(opt) => {
                      setMovieSel(opt);
                      setShowtimeSel(null);
                      setShowtimes([]);
                    }}
                    placeholder="Select Movie"
                    isDisabled={!dateSel || loadingMovies2}
                    isLoading={loadingMovies2}
                    isSearchable
                    noOptionsMessage={() => "No movies available"}
                    loadingMessage={() => "Loading…"}
                  />
                  <Select
                    {...sharedProps}
                    key="ci-time"
                    options={showtimes}
                    value={showtimeSel}
                    onChange={setShowtimeSel}
                    placeholder="Select Time"
                    isDisabled={!movieSel || loadingShowtimes}
                    isLoading={loadingShowtimes}
                    isSearchable={false}
                    noOptionsMessage={() => "No showtimes"}
                    loadingMessage={() => "Loading…"}
                  />
                </>
              )}
            </div>
          )}

          {/* Book Now */}
          {tab && ( //quickbook initial mobile change
            <div className="form-btn">
              <button
                type="submit"
                className="primary-btn justify-content-center"
                disabled={!canSubmit}
              >
                Book Now
              </button>
              {!isDesktop && (
                <button
                  type="button"
                  className="primary-btn justify-content-center qb-cancel-btn"
                  onTouchEnd={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    switchTab(e, tab);
                    if (closeMobileQB) closeMobileQB();
                  }}
                >
                  Close
                </button>
              )}
            </div>
          )}
        </form>
      </div>

      <AgeWarningModal
        isOpen={ageModal}
        onClose={() => {
          setAgeModal(false);
          setPendingNav(null);
        }}
        onConfirm={() => {
          setAgeModal(false);
          if (pendingNav) {
            doNavigate(pendingNav);
            setPendingNav(null);
          }
        }}
      />
    </>
  );
}
